/*
 * -----------------------------------------------------------------------
 *  <copyright file="scoring-label-template.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import React, { useState, useEffect } from 'react';

import CharactersRemaining from '@components/core/characters-remaining';
import ExpandableItem from '@components/core/expandable/item';
import TextField from '@components/core/text-field';
import { useTranslation, useBoolean } from '@hooks/index';
import { validateFields } from '@utils/validation';

import '@pages/admin/organisations/admin-evaluation-models/scoring-label-template/styles.scss';

const ScoringLabelTemplate = ({
    items,
    customKey,
    header,
    onChangeItem,
    field,
    includeDetectability,
    sectionsFieldValidation,
}: any) => {
    const { t } = useTranslation();
    const disabledSave = useBoolean(true);

    const [errors, setErrors] = useState([]);
    useEffect(() => {
        if (items && items.length && !errors.length) {
            const errorArray = [...errors];
            items.forEach(() => {
                errorArray.push({
                    label: null,
                    description: null,
                });
                setErrors(errorArray);
            });
        }
    }, [items]);

    const onChange = (name: string, index: number) => (event: any) => {
        disabledSave.setFalse();
        let length = 20;
        if (name === 'description') {
            length = 220;
        }

        const array = [...items];
        array[index] = {
            ...array[index],
            [name]: event.target.value,
        };
        onChangeItem(index, name, field, event.target.value);
        validate(event.target.value, name, length, index);
    };

    const validate = (value?: string, validateField?: string, length?: number, index?: number) => {
        const error = validateFields(value, validateField, length, false);
        if (error) {
            const array = [...errors];
            array[index] = {
                ...array[index],
                [validateField]: error[validateField],
            };
            setErrors(array);
        }
        sectionsFieldValidation(field, validateField, index, error);
    };

    return (
        <ExpandableItem header={header} key={customKey} isExpanded>
            <div className="scoring-children">
                {items &&
                    items.length &&
                    items.map((item: any, index: number) => (
                        <div className="scoring-item" key={`item-${index}-${customKey}`}>
                            <span className="scoring-number">{index + 1}</span>
                            <TextField
                                id={`scoring-label-${index}-${customKey}`}
                                label={t('label')}
                                multiline
                                rows={4}
                                fullWidth
                                value={item.label ?? ''}
                                onChange={onChange('label', index)}
                                size="small"
                                disabled={!item.enabled || includeDetectability === false}
                                placeholder={t('enterLabel')}
                                helperText={
                                    <CharactersRemaining
                                        id="scoring-label-template-label"
                                        length={item.label?.length ?? 0}
                                        max={20}
                                        error={errors[index] ? errors[index].label : null}
                                    />
                                }
                                variant="outlined"
                                color="secondary"
                                className="mb mr"
                                error={errors[index] && !!errors[index].label}
                            />
                            <TextField
                                id={`scoring-descr-${index}-${customKey}`}
                                label={t('description')}
                                multiline
                                rows={4}
                                fullWidth
                                value={item.description ?? ''}
                                onChange={onChange('description', index)}
                                size="small"
                                disabled={!item.enabled || includeDetectability === false}
                                placeholder={t('enterDescription')}
                                helperText={
                                    <CharactersRemaining
                                        id="scoring-label-template-description"
                                        length={item.description?.length ?? 0}
                                        max={220}
                                        error={errors[index] ? errors[index].description : null}
                                    />
                                }
                                variant="outlined"
                                color="secondary"
                                className="mb"
                                error={errors[index] && !!errors[index].description}
                            />
                        </div>
                    ))}
            </div>
        </ExpandableItem>
    );
};

export default ScoringLabelTemplate;
